"use client";
import { useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import styles from "../../../styles/patient-page-modules/PatientDetail.module.css";
import Spinner from "../../../components/spinner";
import Reload from "../../../components/ui/reload-data";
import { getPatientGeneralDetails, getPatientMLSummary, getCaseManagerUnreviewedPatients } from "../../../services/patient/details"; // getCaseManagerUnreviewedPatients kept only for its tenant_name side effect below
import { getUserFromStorage, getUserIdentity } from "../../../utils/authorization";
import { getCaseManagers, getCaseManagerPatientStats } from "../../../services/case-manager/case-manager";
import { setPatientLocationId } from "../../../utils/helper";

import layoutStyles from "./DailiesDetailsLayout.module.css";
import DailiesPatientInfo from "./components/DailiesPatientInfo";
import DailiesReadingsPanel from "./components/DailiesReadingsPanel";
import DailiesRightPanel from "./components/DailiesRightPanel";
import CompletionModal from "./components/CompletionModal";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../../components/ui/select";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import Button from "../../../components/button-v1";
import { toast } from "react-hot-toast";

const STORAGE_KEYS = {
  PATIENT_SEQUENCE: "_dp",
  PATIENT_ID: "_pt_id",
  DAILIES_INITIAL_TOTAL: "_dailies_initial_total",
  DAILIES_REVIEWED_COUNT: "_dailies_reviewed_count",
};

const DailiesPatientDetails = ({ timerValues = {} }) => {
  const [searchParams, setQueryParams] = useSearchParams();
  const navigate = useNavigate();
  const [selectedCaseManager, setSelectedCaseManager] = useState(null);
  const [navigationState, setNavigationState] = useState({
    currentPatientIndex: 0,
    patientSequence: [],
    patientId: null,
    initialLoadComplete: false,
  });
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [initialTotal, setInitialTotal] = useState(0);
  const [reviewedCount, setReviewedCount] = useState(0);
  const [hasUnsavedReadings, setHasUnsavedReadings] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);

  const user = getUserFromStorage();
  const userIdentity = getUserIdentity(user);
  const isCareManager = userIdentity?.isCareManager || false;
  const currentStaffId = user?.staff_id;

  const careManagerName = selectedCaseManager
    ? `${selectedCaseManager.first_name} ${selectedCaseManager.last_name}`
    : isCareManager && user
      ? `${user.first_name} ${user.last_name}`
      : "Care Manager";

  // ── Data fetching ────────────────────────────────────────────────────────────

  const { data: caseManagers = [], isLoading: cmLoading } = useQuery(
    ["case-managers"],
    () => getCaseManagers(),
    { staleTime: 5 * 60 * 1000 }
  );

  // Auto-select logged-in case manager
  useEffect(() => {
    if (caseManagers.length > 0 && !selectedCaseManager && isCareManager && user) {
      // Match by email to avoid tenant staff_id mismatch issues
      const targetEmail = user.email?.toLowerCase();
      const match = caseManagers.find((cm) => cm.email?.toLowerCase() === targetEmail);
      if (match) setSelectedCaseManager(match);
    }
  }, [caseManagers, user, isCareManager]);

  const activeStaffId =
    selectedCaseManager?.id ||
    selectedCaseManager?.staff_id ||
    currentStaffId;

  // Kept only for its tenant_name sessionStorage side effect (used as a
  // fallback in DailiesPatientInfo) — no longer the source of the nav list.
  useQuery(
    ["case-manager-unreviewed-patients", activeStaffId],
    () => getCaseManagerUnreviewedPatients(activeStaffId),
    {
      enabled: !!activeStaffId,
      staleTime: 0,
      onSuccess: (data) => {
        if (data?.tenant_name) {
          sessionStorage.setItem("tenant_name", data.tenant_name);
        }
      },
    }
  );

  const {
    data: cmStats,
    isLoading: statsLoading,
    isFetched: statsFetched,
    refetch: refetchStats,
  } = useQuery(
    ["case-manager-stats", activeStaffId],
    () => getCaseManagerPatientStats(activeStaffId),
    { enabled: !!activeStaffId, staleTime: 0, refetchOnWindowFocus: false }
  );

  // ── Patient list ─────────────────────────────────────────────────────────────
  // Sourced from the patient-stats API's own patient_ids so the navigation
  // sequence and the progress total can never drift out of sync (they used
  // to come from two independently-scoped endpoints).
  const patientList = useMemo(() => {
    if (!Array.isArray(cmStats?.patient_ids)) return [];
    return cmStats.patient_ids
      .filter((id) => id !== null && id !== undefined && id !== "")
      .map(String);
  }, [cmStats]);

  // ── Clear sessionStorage on mount (fresh start on page reload) ───────────────
  useEffect(() => {
    // Always clear on mount to ensure fresh start on page reload
    sessionStorage.removeItem(STORAGE_KEYS.PATIENT_ID);
    sessionStorage.removeItem(STORAGE_KEYS.PATIENT_SEQUENCE);
    sessionStorage.removeItem(STORAGE_KEYS.DAILIES_INITIAL_TOTAL);
    sessionStorage.removeItem(STORAGE_KEYS.DAILIES_REVIEWED_COUNT);
    setInitialTotal(0);
    setReviewedCount(0);
  }, []);

  // ── Initial total tracking (set once the nav queue resolves) ─────────────────────
  // Derived from patientSequence (built from cmStats.patient_ids) rather than
  // the separate total_patients_with_unreviewed_readings count field — those
  // two fields on the same response aren't guaranteed to agree, and a
  // mismatch there is exactly what produces a header showing "N patients"
  // while the body shows an empty queue.

  useEffect(() => {
    if (!navigationState.initialLoadComplete) return;

    // Set initial total if not already set (first load for this session)
    if (initialTotal === 0 && navigationState.patientSequence.length > 0) {
      const count = navigationState.patientSequence.length;
      sessionStorage.setItem(STORAGE_KEYS.DAILIES_INITIAL_TOTAL, count);
      setInitialTotal(count);
    }
  }, [navigationState.initialLoadComplete, navigationState.patientSequence]);

  // ── Completion modal logic ─────────────────────────────────────────────────────

  useEffect(() => {
    // Show modal when:
    // 1. We have an initial total > 0 (started with patients)
    // 2. Patient list is now empty (all reviewed)
    // 3. Navigation is complete
    // 4. Not currently loading (prevents false positives during refetch)
    if (initialTotal > 0 && navigationState.initialLoadComplete && patientList.length === 0 && !statsLoading) {
      setShowCompletionModal(true);
    } else {
      setShowCompletionModal(false);
    }
  }, [initialTotal, navigationState.initialLoadComplete, patientList.length, statsLoading]);

  // ── Navigation sequence management ────────────────────────────────────────────────

  useEffect(() => {
    if (!navigationState.initialLoadComplete) return;
    // Don't clear sequence during loading/refetch to prevent false empty states
    if (statsLoading) return;

    const newSequence = patientList;
    const currentSequence = navigationState.patientSequence;

    const sequencesChanged =
      newSequence.length !== currentSequence.length ||
      newSequence.some((id, idx) => id !== currentSequence[idx]);

    if (sequencesChanged) {
      // Any patient who was in the previous queue but has dropped out of the
      // fresh one has had all their unreviewed readings addressed (Reviewed
      // or Escalated) — count and persist that explicitly rather than
      // deriving it from initialTotal - currentUnreviewed, which can drift
      // if the stats refetch lags behind the queue.
      const justCompleted = currentSequence.filter(
        (id) => !newSequence.includes(id)
      );
      if (justCompleted.length > 0) {
        setReviewedCount((prev) => {
          const next = prev + justCompleted.length;
          sessionStorage.setItem(STORAGE_KEYS.DAILIES_REVIEWED_COUNT, next);
          return next;
        });
      }

      sessionStorage.setItem(STORAGE_KEYS.PATIENT_SEQUENCE, JSON.stringify(newSequence));

      const currentPatientId = navigationState.patientId;
      const newIndex = newSequence.includes(currentPatientId)
        ? newSequence.indexOf(currentPatientId)
        : 0;

      const newPatientId = newSequence[newIndex] || null;

      if (newPatientId) {
        sessionStorage.setItem(STORAGE_KEYS.PATIENT_ID, newPatientId);
        setNavigationState((prev) => ({
          ...prev,
          patientSequence: newSequence,
          currentPatientIndex: newIndex,
          patientId: newPatientId,
        }));
      } else {
        // Only clear when truly done (not loading AND list is empty)
        if (!statsLoading && newSequence.length === 0) {
          // No patients left - clear sessionStorage, reset navigation, and clean URL
          sessionStorage.removeItem(STORAGE_KEYS.PATIENT_ID);
          sessionStorage.removeItem("_pt_loc_id");
          navigateToCleanUrl(); // Remove patient_id from URL
          setNavigationState((prev) => ({
            ...prev,
            patientSequence: [],
            currentPatientIndex: 0,
            patientId: null,
          }));
        }
      }
    }
  }, [patientList, statsLoading]);

  // ── Navigation initialization ───────────────────────────────────────────────────

  const patientIdFromUrl = searchParams.get("patient_id");

  const dataReady =
    !cmLoading &&
    (!activeStaffId || statsFetched);

  // On first load we never trust a patient_id already present (URL, or
  // sessionStorage) — it may be stale from a previous session, a bookmark,
  // or browser back/forward. The session always starts at the first patient
  // in the freshly-fetched stats queue, and the URL is overwritten to match.
  useEffect(() => {
    if (navigationState.initialLoadComplete) return;
    if (!dataReady) return;

    const sequence = patientList;
    sessionStorage.setItem(STORAGE_KEYS.PATIENT_SEQUENCE, JSON.stringify(sequence));

    const firstId = sequence[0] ?? null;

    if (firstId) {
      sessionStorage.setItem(STORAGE_KEYS.PATIENT_ID, firstId);
    } else {
      sessionStorage.removeItem(STORAGE_KEYS.PATIENT_ID);
    }

    setQueryParams((prev) => {
      const params = new URLSearchParams(prev);
      if (firstId) {
        params.set("patient_id", firstId);
      } else {
        params.delete("patient_id");
      }
      return params;
    });

    setNavigationState({
      currentPatientIndex: 0,
      patientSequence: sequence,
      patientId: firstId,
      initialLoadComplete: true,
    });
  }, [dataReady, navigationState.initialLoadComplete, patientList]);

  useEffect(() => {
    if (!navigationState.initialLoadComplete || !patientIdFromUrl) return;

    setNavigationState((prev) => {
      // Never adopt a patient_id from the URL that isn't in the known
      // queue — a stale/invalid value must not overwrite a valid patient.
      if (!prev.patientSequence.includes(patientIdFromUrl)) return prev;
      if (prev.patientId === patientIdFromUrl) return prev;
      return {
        ...prev,
        patientId: patientIdFromUrl,
        currentPatientIndex: prev.patientSequence.indexOf(patientIdFromUrl),
      };
    });
  }, [patientIdFromUrl, navigationState.initialLoadComplete]);

  // ── Patient detail queries ───────────────────────────────────────────────────────

  const { data: patient, error, isLoading, refetch } = useQuery({
    queryKey: ["patient-details", navigationState.patientId],
    queryFn: () => getPatientGeneralDetails(navigationState.patientId),
    enabled: !!navigationState.patientId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    retryDelay: (i) => Math.min(1000 * 2 ** i, 30000),
  });

  const { data: mlSummary, isLoading: summaryLoading } = useQuery({
    queryKey: ["patient-ml-summary", navigationState.patientId],
    queryFn: () => getPatientMLSummary(navigationState.patientId),
    enabled: !!navigationState.patientId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
  });

  useEffect(() => {
    if (patient) {
      setQueryParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set("RPM", Number(patient.rpm));
        return params;
      });
    }
  }, [patient]);

  useEffect(() => {
    // Only set location ID if we have a valid ID
    // This prevents setting null/undefined values during initial load
    if (patient?.location?.id) {
      setPatientLocationId(patient.location.id);
    }
  }, [patient?.location?.id]);

  // ── Handle unsaved readings changes ───────────────────────────────────────────────
  const handleUnsavedChangesChange = (hasUnsaved) => {
    setHasUnsavedReadings(hasUnsaved);
  };

  const confirmNavigation = () => {
    setShowUnsavedWarning(false);
    setIsNavigating(true);
    if (pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
      // Reset navigating flag after navigation completes
      setTimeout(() => setIsNavigating(false), 100);
    }
  };

  const cancelNavigation = () => {
    setShowUnsavedWarning(false);
    setPendingNavigation(null);
  };

  // ── Navigation helpers ─────────────────────────────────────────────────────────

  // Navigate to clean URL (no patient_id parameter)
  const navigateToCleanUrl = () => {
    // Remove patient_id from URL while preserving other params like RPM
    setQueryParams((prev) => {
      const params = new URLSearchParams(prev);
      params.delete("patient_id");
      return params;
    });
  };

  const navigateToPatient = (patientId) => {
    sessionStorage.setItem(STORAGE_KEYS.PATIENT_ID, patientId);
    // Update URL with patient_id
    setQueryParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("patient_id", patientId);
      return params;
    });
  };

  const goToPreviousPatient = () => {
    // Prevent navigation if already navigating or at the start
    if (isNavigating || navigationState.currentPatientIndex <= 0) return;

    const navigateAction = () => {
      setIsNavigating(true);
      navigateToPatient(navigationState.patientSequence[navigationState.currentPatientIndex - 1]);
      refetchStats();
      // Reset navigating flag after navigation completes
      setTimeout(() => setIsNavigating(false), 100);
    };

    if (hasUnsavedReadings) {
      setPendingNavigation(() => navigateAction);
      setShowUnsavedWarning(true);
    } else {
      navigateAction();
    }
  };

  const goToNextPatient = () => {
    // Prevent navigation if already navigating or at the end
    const isLastPatient = navigationState.currentPatientIndex >= navigationState.patientSequence.length - 1;
    if (isNavigating || isLastPatient) return;

    const navigateAction = () => {
      setIsNavigating(true);
      navigateToPatient(navigationState.patientSequence[navigationState.currentPatientIndex + 1]);
      refetchStats();
      // Reset navigating flag after navigation completes
      setTimeout(() => setIsNavigating(false), 100);
    };

    if (hasUnsavedReadings) {
      setPendingNavigation(() => navigateAction);
      setShowUnsavedWarning(true);
    } else {
      navigateAction();
    }
  };

  const handleCaseManagerChange = (value) => {
    const cm =
      caseManagers.find((c) => String(c.id) === String(value)) ||
      caseManagers.find((c) => String(c.staff_id) === String(value));
    if (!cm) return;

    setSelectedCaseManager(cm);
    setNavigationState({
      currentPatientIndex: 0,
      patientSequence: [],
      patientId: null,
      initialLoadComplete: false,
    });
    setShowCompletionModal(false);
    setInitialTotal(0);
    setReviewedCount(0);

    // Clear all sessionStorage
    sessionStorage.removeItem(STORAGE_KEYS.PATIENT_ID);
    sessionStorage.removeItem(STORAGE_KEYS.PATIENT_SEQUENCE);
    sessionStorage.removeItem(STORAGE_KEYS.DAILIES_INITIAL_TOTAL);
    sessionStorage.removeItem(STORAGE_KEYS.DAILIES_REVIEWED_COUNT);
    sessionStorage.removeItem("_pt_loc_id");

    // Navigate to clean URL (remove patient_id from URL)
    navigateToCleanUrl();
  };

  const handleBackToPatients = () => {
    sessionStorage.setItem("my-tasks/active-tab", "priority");
    // Navigate to clean URL first
    navigateToCleanUrl();
    // Then navigate to /my-tasks and switch tab
    navigate("/my-tasks");
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent("switch-my-tasks-tab", { detail: { tab: "priority" } }));
    }, 0);

    setShowCompletionModal(false);
    setInitialTotal(0);
    setReviewedCount(0);

    // Clear all sessionStorage
    sessionStorage.removeItem(STORAGE_KEYS.PATIENT_ID);
    sessionStorage.removeItem(STORAGE_KEYS.PATIENT_SEQUENCE);
    sessionStorage.removeItem(STORAGE_KEYS.DAILIES_INITIAL_TOTAL);
    sessionStorage.removeItem(STORAGE_KEYS.DAILIES_REVIEWED_COUNT);
    sessionStorage.removeItem("_pt_loc_id");
  };

  // ── Progress calculation ───────────────────────────────────────────────────────

  // patientSequence (via initialTotal) is the only source of truth for the
  // total — total_patients_with_unreviewed_readings is a separate stats
  // field that isn't guaranteed to agree with the actual queue, and falling
  // back to it here reintroduces the same header/body mismatch on the
  // empty-at-start path.
  const displayTotal = initialTotal;
  // reviewedCount is tracked explicitly (state, persisted below) instead of
  // derived from initialTotal - currentUnreviewed, so it can never drift if
  // the stats refetch lags or disagrees with the queue.
  // Calculate progress, ensuring we handle the no-patients case
  const progress = displayTotal > 0 ? (reviewedCount / displayTotal) * 100 : 0;
  // Determine if progress should be shown (hide when no patients at all)
  const shouldShowProgress = displayTotal > 0;

  // ── Render guards ────────────────────────────────────────────────────────────────

  if (error) {
    return (
      <div className={styles.patient_page}>
        <div className={styles.patient_page_container}>
          <Reload message="Could not load patient details" onReload={refetch} />
        </div>
      </div>
    );
  }

  if (!dataReady || !navigationState.initialLoadComplete) {
    return (
      <div className={layoutStyles.patient_page}>
        <div className={layoutStyles.pageHeader}>
          <div className={layoutStyles.headerLeft}><h3>{careManagerName}</h3></div>
          <div className={layoutStyles.headerCenter}>
            <span className={layoutStyles.progressLabel}>Loading…</span>
          </div>
          <div className={layoutStyles.headerRight} />
        </div>
        <div style={{ display: "flex", justifyContent: "center", padding: "60px" }}>
          <Spinner />
        </div>
      </div>
    );
  }

  const emptyMessage = selectedCaseManager
    ? `No patients with readings found for ${selectedCaseManager.first_name} ${selectedCaseManager.last_name}`
    : isCareManager
      ? "No patients with readings assigned to you in the last 24 hours"
      : "No patients with readings in the last 24 hours";

  const showEmptyState = navigationState.initialLoadComplete && !navigationState.patientId;
  const patientLoading = isLoading || !navigationState.patientId;

  const selectValue =
    selectedCaseManager?.id ||
    selectedCaseManager?.staff_id ||
    currentStaffId ||
    "";

  return (
    <div className={layoutStyles.patient_page}>

      {/* ── Page header ── */}
      <div className={layoutStyles.pageHeader}>
        <div className={layoutStyles.headerLeft}>
          <h3>{careManagerName}</h3>
        </div>

        <div className={layoutStyles.headerCenter}>
          {shouldShowProgress ? (
            <>
              <span className={layoutStyles.progressLabel}>
                {Math.round(progress)}% completed
              </span>
              <div className={layoutStyles.progressWrapper}>
                <div className={layoutStyles.progressFill} style={{ width: `${progress}%` }} />
              </div>
              <span className={layoutStyles.patientCount}>
                {reviewedCount}/{displayTotal} Patients
              </span>
            </>
          ) : (
            <span className={layoutStyles.progressLabel}>
              No patients to review
            </span>
          )}
        </div>

        <div className={layoutStyles.headerRight}>
          <Select
            disabled={cmLoading || caseManagers.length === 0}
            value={String(selectValue)}
            onChange={handleCaseManagerChange}
          >
            <SelectTrigger className={layoutStyles.managerSelectTrigger}>
              <SelectValue>
                {cmLoading
                  ? "Loading…"
                  : caseManagers.length === 0
                    ? "No case managers"
                    : selectedCaseManager
                      ? `${selectedCaseManager.first_name} ${selectedCaseManager.last_name}`
                      : isCareManager && user
                        ? `${user.first_name} ${user.last_name}`
                        : "Select case manager"}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {caseManagers.map((cm) => (
                <SelectItem key={cm.id || cm.staff_id} value={String(cm.id || cm.staff_id)}>
                  {cm.first_name} {cm.last_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* ── Main content ── */}
      <div className={styles.patient_page_container}>
        {showEmptyState ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "40px", color: "#6b7280", fontSize: "14px" }}>
            {emptyMessage}
          </div>
        ) : patientLoading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "60px" }}>
            <Spinner />
          </div>
        ) : (
          <div className={layoutStyles.dailiesDetailsGrid}>

            <div className={layoutStyles.navColumn}>
              <button
                type="button"
                className={layoutStyles.navCircularBtn}
                onClick={goToPreviousPatient}
                disabled={navigationState.currentPatientIndex === 0}
                aria-label="Previous patient"
                title="Previous patient"
              >‹</button>
            </div>

            <div className={layoutStyles.leftColumn}>
              <DailiesPatientInfo patientId={navigationState.patientId} />
              <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
                <DailiesReadingsPanel
                  patientId={navigationState.patientId}
                  onReadingsReviewed={() => {
                    refetchStats();
                  }}
                  onUnsavedChangesChange={handleUnsavedChangesChange}
                />
              </div>
            </div>

            <div className={layoutStyles.rightColumn}>
              <DailiesRightPanel
                patientId={navigationState.patientId}
                summary={mlSummary || patient?.summary}
                summaryLoading={summaryLoading || isLoading}
                noteType="rpm_note"
                caseManager={true}
                noteDropdownLabel="Pre-completed note"
                rpmTimeFormat="HH:MM"
                showAiInsights={true}
                defaultTimer={timerValues}
                resetTimer={() => { }}
              />
            </div>

            <div className={layoutStyles.navColumn}>
              <button
                type="button"
                className={layoutStyles.navCircularBtn}
                onClick={goToNextPatient}
                disabled={navigationState.patientSequence.length === 0 || navigationState.currentPatientIndex >= navigationState.patientSequence.length - 1}
                aria-label="Next patient"
                title="Next patient"
              >›</button>
            </div>

          </div>
        )}
      </div>

      {/* Completion Modal */}
      <CompletionModal
        isOpen={showCompletionModal}
        totalPatients={initialTotal}
        onBackToPatients={handleBackToPatients}
        onClose={() => {
          // Close modal and clean URL
          setShowCompletionModal(false);
          navigateToCleanUrl();
        }}
      />

      {/* Unsaved Changes Warning Dialog */}
      <Dialog open={showUnsavedWarning} setOpen={setShowUnsavedWarning}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unsaved Changes</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p style={{ color: "#6b7280", fontSize: "14px" }}>
              You have unsaved reading status changes. Your changes will be lost.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button
              onClick={cancelNavigation}
              style={{
                backgroundColor: "#fff",
                color: "#6b7280",
                border: "1px solid #d1d5db",
              }}
            >
              Stay Here
            </Button>
            <Button
              onClick={confirmNavigation}
              style={{
                backgroundColor: "#ef4444",
                borderColor: "#ef4444",
              }}
            >
              Leave Anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DailiesPatientDetails;
